import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { fetchAll } from '../../lib/save';
import ListGrid from '../../components/ListGrid';
import PageLoading from '../../components/PageLoading';
import { useToast } from '../../components/ToastContext';
import PageBar from '../../components/PageBar';
import { useAuth } from '../../components/AuthContext';

/*
  SETUP -> PLACE MAPPING, 4 October 2026, moved 6 October.

  **Built as the Mapping tab on Operations -> Warehouses**, and moved into the
  Setup area on 6 October 2026 on the owner's word: the mapping tools are for
  the first setup only, *"I truly just want the mappings for the initial system
  setup and then I want them gone"*, so they live together in one area he alone
  sees and can switch off in Site Settings.

  **One place, a different name in every system.** The owner's instruction: *"we
  would need to create a master list for these similarly to what we did for item.
  Where we have a OnHand standard name, then claimsoft referral source name,
  claimsoft warehouse name, account edge customer name."*

  952 places are loaded. 483 are on both of ClaimSoft's lists, 99 are warehouses
  with no referral source, 370 are referral sources with no warehouse. **620
  already carry an Account Edge customer** from the region rules he gave, and
  **332 do not**, which is East Texas, Austin, and the regions he has not ruled
  on. Those 332 are the work this screen exists for.

  **It changes nothing about what a place IS.** The OnHand name, the ClaimSoft
  names and the flags are all read only here; the one thing that can be set is
  which Account Edge customer a place bills to, because that is the part that
  lives in his head rather than in either system.

  **The card id matters as much as the name.** His words: *"yes i want it mapped
  to an account edge customer by name but i also need to have it map to the
  account edge customer card id which for oklahoma would be 9900."* So the picker
  is the card, and the name travels with it.
*/

const FILTERS = [
  { key: 'no_ae',     label: 'No Account Edge customer' },
  { key: 'one_sided', label: 'On one ClaimSoft list only' },
  { key: 'retired',   label: 'Retired' },
  { key: 'mains',     label: 'Regional mains' },
  { key: 'all',       label: 'Everything' },
];

export async function loadPlaces() {
  const { data, error } = await fetchAll(() => supabase
    .from('warehouses')
    .select('id, onhand_name, region_prefix, region_name, name, claimsoft_referral_name, '
          + 'account_edge_customer, account_edge_card_id, in_warehouse_list, in_referral_list, '
          + 'is_regional_main, is_inactive, city, state')
    .not('onhand_name', 'is', null)
    .order('onhand_name'));
  return { rows: data || [], error };
}

/*
  The Account Edge customers, from his own export of the customer list.

  **It was built from the 65 shipping addresses until 4 October 2026**, which
  knew only 16 cards. He asked the obvious question: *"what if i want to assign
  an account edge customer to a location but that account edge customer is not in
  the list."* It reads the real list now, 667 cards.

  **Retired customers are kept and marked**, not hidden, because a place may
  already point at one and hiding it would make that look like nothing was set.
*/
export async function loadCustomers() {
  const { data, error } = await fetchAll(() => supabase
    .from('account_edge_customers')
    .select('card_id, name, is_inactive, city, state')
    .order('name'));
  return { customers: data || [], error };
}

export function stateOf(p) {
  if (!p.account_edge_customer) return 'no_ae';
  if (!p.in_warehouse_list || !p.in_referral_list) return 'one_sided';
  return 'done';
}

export function tally(rows = []) {
  const out = { total: rows.length, noAe: 0, oneSided: 0, retired: 0, mains: 0 };
  for (const p of rows) {
    if (!p.account_edge_customer) out.noAe += 1;
    if (!p.in_warehouse_list || !p.in_referral_list) out.oneSided += 1;
    if (p.is_inactive) out.retired += 1;
    if (p.is_regional_main) out.mains += 1;
  }
  return out;
}

export default function PlaceMapping({ canEdit = false, search = '' }) {
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [filter, setFilter] = useState('no_ae');
  const [busy, setBusy] = useState(null);
  const [failed, setFailed] = useState(null);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([loadPlaces(), loadCustomers()]);
    /* **A read that fails is not a list that is empty.** Drawing one as the
       other is this project's oldest fault, so an error says so and offers
       another go rather than drawing "nothing to map". */
    if (a.error) { setFailed(a.error.message); setRows([]); return; }
    setFailed(null);
    setRows(a.rows);
    setCustomers(b.customers);
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => (rows ? tally(rows) : null), [rows]);

  const shown = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();
    return rows.filter(p => {
      if (filter === 'no_ae' && p.account_edge_customer) return false;
      if (filter === 'one_sided' && p.in_warehouse_list && p.in_referral_list) return false;
      if (filter === 'retired' && !p.is_inactive) return false;
      if (filter === 'mains' && !p.is_regional_main) return false;
      if (!q) return true;
      return (p.onhand_name || '').toLowerCase().includes(q)
        || (p.name || '').toLowerCase().includes(q)
        || (p.claimsoft_referral_name || '').toLowerCase().includes(q)
        || (p.account_edge_customer || '').toLowerCase().includes(q);
    });
  }, [rows, filter, search]);

  /*
    **The write is read back.** A save that no rule allows changes no rows,
    raises nothing and answers "fine", which has cost this project a day more
    than once. Nothing on screen moves until the row comes back saying what it
    now holds.
  */
  const assign = useCallback(async (place, cardId) => {
    setBusy(place.id);
    const customer = customers.find(c => c.card_id === cardId) || null;
    const { data, error } = await supabase.from('warehouses')
      .update({
        account_edge_card_id: cardId || null,
        account_edge_customer: customer ? customer.name : null,
      })
      .eq('id', place.id)
      .select('id, account_edge_customer, account_edge_card_id')
      .maybeSingle();
    setBusy(null);
    if (error) { toast.error('Could not save that: ' + error.message); return; }
    if (!data) { toast.error('That did not save. Nothing was changed.'); return; }
    setRows(prev => prev.map(r => (r.id === place.id
      ? { ...r, account_edge_customer: data.account_edge_customer, account_edge_card_id: data.account_edge_card_id }
      : r)));
    toast.success(cardId
      ? place.onhand_name + ' bills to ' + (customer ? customer.name : cardId)
      : place.onhand_name + ' has no Account Edge customer');
  }, [customers, toast]);

  if (rows === null) return <PageLoading rows={8} />;

  if (failed) {
    return (
      <div className="card" style={{ padding: 24 }}>
        <p>OnHand could not read the places just now.</p>
        <p style={{ color: 'var(--text-secondary)' }}>{failed}</p>
        <button type="button" className="btn" onClick={load}>Try again</button>
      </div>
    );
  }

  const columns = [
    { key: 'onhand', label: 'OnHand name',            width: 'minmax(200px, 1.6fr)' },
    { key: 'region', label: 'Region',                 width: '130px' },
    { key: 'cs_wh',  label: 'ClaimSoft warehouse',    width: 'minmax(170px, 1.4fr)' },
    { key: 'cs_rs',  label: 'ClaimSoft referral source', width: 'minmax(170px, 1.4fr)' },
    { key: 'ae',     label: 'Account Edge customer',  width: 'minmax(210px, 1.6fr)' },
  ];

  const missing = <span style={{ color: 'var(--warning-ink)', fontWeight: 700, fontSize: 12 }}>not on this list</span>;

  const cells = p => [
    <span>
      <span className="rpnum">{p.onhand_name}</span>
      {p.is_regional_main && <span className="oh-pill-soon" style={{ marginLeft: 8 }}>Regional main</span>}
      {p.is_inactive && (
        <span className="oh-pill-soon" style={{ marginLeft: 8 }}
          title="ClaimSoft marks this retired, as DNU or inactive. It is kept for reference and should not be ordered for.">
          Retired
        </span>
      )}
      {(p.city || p.state) && (
        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2 }}>
          {[p.city, p.state].filter(Boolean).join(', ')}
        </span>
      )}
    </span>,
    p.region_name || p.region_prefix || <span style={{ color: 'var(--text-secondary)' }}>none</span>,
    p.in_warehouse_list ? p.name : missing,
    p.in_referral_list ? p.claimsoft_referral_name : missing,
    <select
      className="fld"
      style={{ maxWidth: 260 }}
      value={p.account_edge_card_id || ''}
      disabled={!canEdit || busy === p.id}
      onChange={e => assign(p, e.target.value || null)}
      aria-label={'Which Account Edge customer does ' + p.onhand_name + ' bill to'}
    >
      <option value="">Not set</option>
      {customers.map(c => (
        <option key={c.card_id} value={c.card_id}>
          {c.name} ({c.card_id}){c.is_inactive ? ' - retired' : ''}
        </option>
      ))}
    </select>,
  ];

  return (
    <>
      <div className="card" style={{ padding: '14px 18px', marginBottom: 16, display: 'flex', gap: 28, flexWrap: 'wrap' }}>
        {[
          ['No Account Edge customer', counts.noAe, 'var(--warning-ink)'],
          ['On one list only', counts.oneSided, 'var(--info-ink)'],
          ['Retired', counts.retired, 'var(--text-secondary)'],
          ['Regional mains', counts.mains, 'var(--success-ink)'],
          ['Places in all', counts.total, 'var(--text-primary)'],
        ].map(([label, n, ink]) => (
          <div key={label}>
            <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.1, color: ink }}>{n}</div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginTop: 2 }}>
              {label}
            </div>
          </div>
        ))}
        <div style={{ flex: 1 }} />
        <select className="tb-select" value={filter} onChange={e => setFilter(e.target.value)}
          aria-label="Which places to show" style={{ alignSelf: 'center' }}>
          {FILTERS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
      </div>

      <ListGrid
        columns={columns}
        rows={shown}
        keyOf={p => p.id}
        cells={cells}
        layoutKey="place_mapping"
        layoutTitle="The mapping's columns"
        empty={filter === 'no_ae'
          ? 'Every place has an Account Edge customer.'
          : 'No place matches that.'}
      />
    </>
  );
}

/*
  **The page around it**, since it stopped being a tab on Warehouses. Its own
  title and search box, and only an admin may change a pairing, as before.
*/
export function PlaceMappingPage() {
  const { profile } = useAuth();
  const [search, setSearch] = useState('');
  return (
    <>
      <PageBar
        title="Place Mapping"
        subtitle="One place, and what each system calls it"
        controls={
          <input className="tb-search" placeholder="Search any of the names..."
            value={search} onChange={e => setSearch(e.target.value)} />
        }
      />
      <PlaceMapping canEdit={profile?.role === 'admin'} search={search} />
    </>
  );
}
